import React, { useState, useEffect, useCallback } from 'react'
import { api, getErrorMessage } from '@/shared/api/generated'
import { useAuthStore } from '@/features/auth/stores/authStore'
import type { Tag, Value } from '@/shared/api/generated'
import './registry.css'

export function RegistryTagAdmin() {
  const user = useAuthStore(s => s.user)
  const canEditTags = user?.permissions?.canEditTags ?? false
  const canEditVals = user?.permissions?.canEditVals ?? false

  const [tags, setTags] = useState<Tag[]>([])
  const [expandedTag, setExpandedTag] = useState<number | null>(null)
  const [values, setValues] = useState<Value[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // New tag form
  const [newTag, setNewTag] = useState({ name: '', required: false, editable: false, multiple: false, sort_order: 0 })
  // Edit tag
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  // New value form
  const [newValue, setNewValue] = useState({ value: '', persistent: true, group_id: 0 })
  // Edit value
  const [editingValue, setEditingValue] = useState<Value | null>(null)

  const flash = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000) }

  const loadTags = useCallback(async () => {
    if (tags.length === 0) setLoading(true)
    try {
      const t = await api.listTags()
      setTags(t)
    } catch (err) { setError(getErrorMessage(err)) }
    finally { setLoading(false) }
  }, [tags.length])

  useEffect(() => { loadTags() }, [loadTags])

  const loadValues = useCallback(async (tagId: number) => {
    try {
      const v = await api.listValues(tagId)
      setValues(v)
    } catch (err) { setError(getErrorMessage(err)) }
  }, [])

  const toggleTag = (tagId: number) => {
    if (expandedTag === tagId) {
      setExpandedTag(null)
      setValues([])
    } else {
      setExpandedTag(tagId)
      loadValues(tagId)
    }
    setEditingValue(null)
    setNewValue({ value: '', persistent: true, group_id: 0 })
  }

  // --- Tag CRUD ---

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return
    try {
      await api.createTag({ ...newTag, sort_order: newTag.sort_order || tags.length + 1 })
      setNewTag({ name: '', required: false, editable: false, multiple: false, sort_order: 0 })
      flash('Tag created')
      await loadTags()
    } catch (err) { setError(getErrorMessage(err)) }
  }

  const handleUpdateTag = async () => {
    if (!editingTag) return
    try {
      await api.updateTag(editingTag.id, {
        name: editingTag.name, required: editingTag.required,
        editable: editingTag.editable, multiple: editingTag.multiple,
        sort_order: editingTag.sort_order,
      })
      setEditingTag(null)
      flash('Tag updated')
      await loadTags()
    } catch (err) { setError(getErrorMessage(err)) }
  }

  const handleDeleteTag = async (id: number) => {
    if (!window.confirm('Delete this tag? All associated values will also be removed.')) return
    try {
      await api.deleteTag(id)
      if (expandedTag === id) { setExpandedTag(null); setValues([]) }
      flash('Tag deleted')
      await loadTags()
    } catch (err) { setError(getErrorMessage(err)) }
  }

  // --- Value CRUD ---

  const handleCreateValue = async () => {
    if (!newValue.value.trim() || !expandedTag) return
    try {
      await api.createValue(expandedTag, newValue)
      setNewValue({ value: '', persistent: true, group_id: 0 })
      flash('Value created')
      await loadValues(expandedTag)
    } catch (err) { setError(getErrorMessage(err)) }
  }

  const handleUpdateValue = async () => {
    if (!editingValue) return
    try {
      await api.updateValue(editingValue.id, { value: editingValue.value, persistent: editingValue.persistent, group_id: 0 })
      setEditingValue(null)
      flash('Value updated')
      if (expandedTag) await loadValues(expandedTag)
    } catch (err) { setError(getErrorMessage(err)) }
  }

  const handleDeleteValue = async (id: number) => {
    if (!window.confirm('Delete this value?')) return
    try {
      await api.deleteValue(id)
      flash('Value deleted')
      if (expandedTag) await loadValues(expandedTag)
    } catch (err) { setError(getErrorMessage(err)) }
  }

  const handlePurge = async (tagId: number) => {
    if (!window.confirm('Purge all non-persistent values with zero references for this tag?')) return
    try {
      const result = await api.purgeValues(tagId)
      flash(`Purged ${result.purged} unused values`)
      if (expandedTag === tagId) await loadValues(tagId)
    } catch (err) { setError(getErrorMessage(err)) }
  }

  if (!canEditTags && !canEditVals) {
    return (
      <div className="page">
        <div className="page-header"><h1>Tag Administration</h1></div>
        <p className="muted">You do not have permission to manage tags or values.</p>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Tag Administration</h1>
        <p className="muted">Manage registry tags and values</p>
      </div>

      {error && <div className="alert error">{error}<button className="dismiss" onClick={() => setError('')}>✕</button></div>}
      {success && <div className="alert success">{success}</div>}

      {loading ? <p className="muted">Loading…</p> : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th className="centered">Required</th>
                <th className="centered">Editable</th>
                <th className="centered">Multiple</th>
                <th className="centered">Order</th>
                {canEditTags && <th className="right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {tags.map(tag => (
                <React.Fragment key={tag.id}>
                {editingTag?.id === tag.id ? (
                  <tr className="changed">
                    <td><input type="text" value={editingTag.name} onChange={e => setEditingTag({ ...editingTag, name: e.target.value })} /></td>
                    <td className="centered"><input type="checkbox" checked={editingTag.required} onChange={e => setEditingTag({ ...editingTag, required: e.target.checked })} /></td>
                    <td className="centered"><input type="checkbox" checked={editingTag.editable} onChange={e => setEditingTag({ ...editingTag, editable: e.target.checked })} /></td>
                    <td className="centered"><input type="checkbox" checked={editingTag.multiple} onChange={e => setEditingTag({ ...editingTag, multiple: e.target.checked })} /></td>
                    <td className="centered"><input type="number" value={editingTag.sort_order} onChange={e => setEditingTag({ ...editingTag, sort_order: Number(e.target.value) })} style={{ width: 50 }} /></td>
                    <td className="right actions">
                      <button className="link success" onClick={handleUpdateTag}>Save</button>
                      <button className="link" onClick={() => setEditingTag(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr className={expandedTag === tag.id ? 'active' : undefined} onClick={() => toggleTag(tag.id)} style={{ cursor: 'pointer' }}>
                    <td><span className="tag-expand-icon">{expandedTag === tag.id ? '▼' : '▶'}</span> {tag.name}</td>
                    <td className="centered">{tag.required ? '✓' : ''}</td>
                    <td className="centered">{tag.editable ? '✓' : ''}</td>
                    <td className="centered">{tag.multiple ? '✓' : ''}</td>
                    <td className="centered">{tag.sort_order}</td>
                    {canEditTags && (
                      <td className="right actions" onClick={e => e.stopPropagation()}>
                        <button className="link" onClick={() => setEditingTag({ ...tag })}>Edit</button>
                        <button className="link danger" onClick={() => handleDeleteTag(tag.id)}>Delete</button>
                        {canEditVals && <button className="link" onClick={() => handlePurge(tag.id)}>Purge</button>}
                      </td>
                    )}
                  </tr>
                )}
                {expandedTag === tag.id && (
                <tr>
                  <td colSpan={canEditTags ? 6 : 5}>
                    <div className="tag-values-panel">
                      <h4>Values for: {tag.name}</h4>
                      <table className="values-table">
                        <thead>
                          <tr>
                            <th>Value</th>
                            <th className="centered">Persistent</th>
                            {canEditVals && <th className="right">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {values.map(val => (
                            editingValue?.id === val.id ? (
                              <tr key={val.id} className="changed">
                                <td><input type="text" value={editingValue.value} onChange={e => setEditingValue({ ...editingValue, value: e.target.value })} /></td>
                                <td className="centered"><input type="checkbox" checked={editingValue.persistent} onChange={e => setEditingValue({ ...editingValue, persistent: e.target.checked })} /></td>
                                <td className="right actions">
                                  <button className="link success" onClick={handleUpdateValue}>Save</button>
                                  <button className="link" onClick={() => setEditingValue(null)}>Cancel</button>
                                </td>
                              </tr>
                            ) : (
                              <tr key={val.id}>
                                <td>{val.value}</td>
                                <td className="centered">{val.persistent ? '✓' : ''}</td>
                                {canEditVals && (
                                  <td className="right actions">
                                    <button className="link" onClick={() => setEditingValue({ ...val })}>Edit</button>
                                    <button className="link danger" onClick={() => handleDeleteValue(val.id)}>Delete</button>
                                  </td>
                                )}
                              </tr>
                            )
                          ))}
                          {canEditVals && (
                            <tr>
                              <td><input type="text" value={newValue.value} onChange={e => setNewValue({ ...newValue, value: e.target.value })} placeholder="New value" onKeyDown={e => { if (e.key === 'Enter') handleCreateValue() }} /></td>
                              <td className="centered"><label className="inline-label"><input type="checkbox" checked={newValue.persistent} onChange={e => setNewValue({ ...newValue, persistent: e.target.checked })} /> Persistent</label></td>
                              <td className="right"><button className="link success" onClick={handleCreateValue} disabled={!newValue.value.trim()}>Add</button></td>
                            </tr>
                          )}
                          {values.length === 0 && !canEditVals && (
                            <tr><td colSpan={2} className="muted">No values</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
                )}
                </React.Fragment>
              ))}

              {/* New tag row */}
              {canEditTags && (
                <tr>
                  <td><input type="text" value={newTag.name} onChange={e => setNewTag({ ...newTag, name: e.target.value })} placeholder="New tag name" onKeyDown={e => { if (e.key === 'Enter') handleCreateTag() }} /></td>
                  <td className="centered"><input type="checkbox" checked={newTag.required} onChange={e => setNewTag({ ...newTag, required: e.target.checked })} /></td>
                  <td className="centered"><input type="checkbox" checked={newTag.editable} onChange={e => setNewTag({ ...newTag, editable: e.target.checked })} /></td>
                  <td className="centered"><input type="checkbox" checked={newTag.multiple} onChange={e => setNewTag({ ...newTag, multiple: e.target.checked })} /></td>
                  <td className="centered"><input type="number" value={newTag.sort_order} onChange={e => setNewTag({ ...newTag, sort_order: Number(e.target.value) })} style={{ width: 50 }} /></td>
                  <td className="right"><button className="link success" onClick={handleCreateTag} disabled={!newTag.name.trim()}>Add</button></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
